// src/pages/NotificationDetail.jsx

import React from "react";
import { Grid, Typography, Button, Chip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useLocation, useNavigate } from "react-router-dom";

/* ========================= */
/* 🔹 Styles */
/* ========================= */
const useStyles = makeStyles({
  label: {
    marginBottom: "8px",
    color: "#1E1E1E",
    fontSize: "16px",
    fontWeight: "500",
  },
  value: {
    color: "#000",
    paddingLeft: "4px",
    fontSize: "15px",
  },
  header: {
    borderBottom: "2px solid",
    marginBottom: "20px",
    paddingBottom: "10px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  buttonWrapper: {
    display: "flex",
    justifyContent: "end",
    marginTop: "30px",
    paddingTop: "20px",
    borderTop: "2px solid",
  },
  backBtn: {
    width: "98px",
    height: "44px",
    textTransform: "capitalize",
    background: "linear-gradient(180deg, #255480 0%, #173450 100%)",
  },
  sectionTitle: {
    marginTop: "24px",
    marginBottom: "16px",
    color: "#255480",
    fontWeight: "600",
    fontSize: "18px",
  },
  statusChip: {
    textTransform: "capitalize",
    fontWeight: "500",
  },
  typeChip: {
    textTransform: "capitalize",
    fontWeight: "500",
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
  },
});

/* ========================= */
/* 🔹 Helper: Render Detail Row */
/* ========================= */
const DetailRow = ({ label, value, classes }) => {
  if (!value || value === "-") return null;
  
  return (
    <Grid item lg={4} md={6} xs={12}>
      <Typography className={classes.label}>{label}</Typography>
      <Typography className={classes.value}>{value}</Typography>
    </Grid>
  );
};

/* ========================= */
/* 🔹 Main Component */
/* ========================= */
export default function NotificationDetail() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const classes = useStyles();

  if (!state) {
    return (
      <div className="min-width">
        <Typography variant="h6" color="error">
          Notification not found
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate(-1)}
          style={{ marginTop: "20px" }}
        >
          Go Back
        </Button>
      </div>
    );
  }

  const metadata = state.metadata || {};
  const isPackageBooking = state.type === "package_booking_success";
  const isDoctorBooking = state.type === "doctor_booking";

  /* ========================= */
  /* 🔹 Render Common Fields */
  /* ========================= */
  const renderCommonDetails = () => (
    <>
      <DetailRow 
        label="Notification Title" 
        value={state.title} 
        classes={classes} 
      />
      <DetailRow 
        label="Message" 
        value={state.body} 
        classes={classes} 
      />
      <DetailRow 
        label="Sent At" 
        value={new Date(state.sentAt).toLocaleString()} 
        classes={classes} 
      />
      <DetailRow 
        label="Created At" 
        value={new Date(state.created_at).toLocaleString()} 
        classes={classes} 
      />
    </>
  );

  /* ========================= */
  /* 🔹 Render Doctor Booking Details */
  /* ========================= */
  const renderDoctorBookingDetails = () => (
    <>
      <Grid item xs={12}>
        <Typography className={classes.sectionTitle}>
          Booking Information
        </Typography>
      </Grid>
      
      <DetailRow 
        label="Booking ID" 
        value={metadata.bookingId} 
        classes={classes} 
      />
      <DetailRow 
        label="Patient Name" 
        value={metadata.patientName || metadata.customerName} 
        classes={classes} 
      />
      <DetailRow 
        label="Patient Phone" 
        value={metadata.patientPhone || metadata.customerPhone} 
        classes={classes} 
      />
      <DetailRow 
        label="Doctor Name" 
        value={metadata.doctorName} 
        classes={classes} 
      />
      <DetailRow 
        label="Hospital" 
        value={metadata.hospitalName || "Not specified"} 
        classes={classes} 
      />
      <DetailRow 
        label="Appointment Date" 
        value={metadata.formattedDate} 
        classes={classes} 
      />
      <DetailRow 
        label="Appointment Time" 
        value={metadata.time} 
        classes={classes} 
      />
      <DetailRow 
        label="Doctor ID" 
        value={metadata.doctorId} 
        classes={classes} 
      />
      <DetailRow 
        label="Hospital ID" 
        value={metadata.hospitalId || "N/A"} 
        classes={classes} 
      />
    </>
  );

  /* ========================= */
  /* 🔹 Render Package Booking Details */
  /* ========================= */
  const renderPackageBookingDetails = () => (
    <>
      <Grid item xs={12}>
        <Typography className={classes.sectionTitle}>
          Package Information
        </Typography>
      </Grid>
      
      <DetailRow 
        label="Booking ID" 
        value={metadata.bookingId} 
        classes={classes} 
      />
      <DetailRow 
        label="Package Name" 
        value={metadata.packageName} 
        classes={classes} 
      />
      <DetailRow 
        label="Package ID" 
        value={metadata.packageId} 
        classes={classes} 
      />
      <DetailRow 
        label="Customer Name" 
        value={metadata.customerName} 
        classes={classes} 
      />
      <DetailRow 
        label="Customer Phone" 
        value={metadata.customerPhone} 
        classes={classes} 
      />
      <DetailRow 
        label="Booked Date" 
        value={metadata.formattedDate} 
        classes={classes} 
      />
      <DetailRow 
        label="Time Slot" 
        value={metadata.slot} 
        classes={classes} 
      />
      
      <Grid item xs={12}>
        <Typography className={classes.sectionTitle}>
          Pricing Details
        </Typography>
      </Grid>
      
      <DetailRow 
        label="Package Price" 
        value={metadata.packagePrice ? `$${metadata.packagePrice}` : "-"} 
        classes={classes} 
      />
      <DetailRow 
        label="Addons Price" 
        value={metadata.addonsPrice ? `$${metadata.addonsPrice}` : "$0.00"} 
        classes={classes} 
      />
      <DetailRow 
        label="Total Price" 
        value={metadata.totalPrice ? `$${metadata.totalPrice}` : "-"} 
        classes={classes} 
      />
      <DetailRow 
        label="Home Collection" 
        value={metadata.homeCollection ? "Yes" : "No"} 
        classes={classes} 
      />
      <DetailRow 
        label="Address" 
        value={metadata.address || "Not provided"} 
        classes={classes} 
      />
    </>
  );

  /* ========================= */
  /* 🔹 Render Generic Metadata */
  /* ========================= */
  const renderGenericMetadata = () => {
    if (!metadata || Object.keys(metadata).length === 0) return null;

    return (
      <>
        <Grid item xs={12}>
          <Typography className={classes.sectionTitle}>
            Additional Information
          </Typography>
        </Grid>
        {Object.entries(metadata).map(([key, value]) => (
          <DetailRow
            key={key}
            label={key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
            value={typeof value === "object" ? JSON.stringify(value) : String(value)}
            classes={classes}
          />
        ))}
      </>
    );
  };

  return (
    <div className="min-width">
      {/* ================= HEADER ================= */}
      <div className={classes.header}>
        <Typography variant="h6">Notification Details</Typography>
        <div>
          <Chip
            label={state.type.replace(/_/g, " ")}
            className={classes.typeChip}
            size="small"
            style={{ marginRight: "8px" }}
          />
          <Chip
            label={state.status}
            className={classes.statusChip}
            color={state.status === "sent" ? "success" : "default"}
            size="small"
            style={{ marginRight: "8px" }}
          />
          {state.isRead && (
            <Chip
              label="Read"
              color="default"
              size="small"
            />
          )}
        </div>
      </div>

      {/* ================= DETAILS GRID ================= */}
      <Grid container spacing={3} alignItems="flex-start">
        {/* Common Details */}
        <Grid item xs={12}>
          <Typography className={classes.sectionTitle}>
            General Information
          </Typography>
        </Grid>
        {renderCommonDetails()}

        {/* Type-Specific Details */}
        {isDoctorBooking && renderDoctorBookingDetails()}
        {isPackageBooking && renderPackageBookingDetails()}
        {!isDoctorBooking && !isPackageBooking && renderGenericMetadata()}
      </Grid>

      {/* ================= FOOTER ================= */}
      <div className={classes.buttonWrapper}>
        <Button
          variant="contained"
          className={classes.backBtn}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </div>
    </div>
  );
}